import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DevLinkProvider, EngineEarsFooter2024 } from "../../../../../devlink";
import {
  EETabs,
  TabsBottomGreyBar,
} from "../../../ProfileScreen/ProfileScreen.styles";
import { a11yProps } from "../../../ProfileScreen/utils";
import {
  BookingsScreenContainer,
  BookingsScreenContainerWrapper,
  BookingsScreenFooterWrapper,
  StyledBookingTab,
} from "../../BookingsScreen.styles";
import { BookingTabs } from "../../constants";
import { Outlet, useMatch, useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import {
  activeProfileAtom,
  activeStudioProfileAtom,
} from "../../../../../atoms/user/activeProfileAtom";
import { ProfileType } from "../../../../../store/models/base";
import { ROUTE_PREFIXES } from "../../../../../routes";
import { BookingsScreenProjectsTabLabel } from "../BookingsScreenProjectsTabLabel/BookingsScreenProjectsTabLabel";
import { BookingsScreenSessionsTabLabel } from "../BookingsScreenSessionsTabLabel/BookingsScreenSessionsTabLabel";
import { emitAnalyticsTrackingEvent } from "../../../../../utils/analyticsUtils";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { selectEngineerServicesForCurrentEngineerLoaded } from "../../../../../store/selectors/servicesSelectors";
import { useSetPageTitle } from "../../../../../hooks/useSetPageTitle";
import { loadEngineerServices } from "../../../../../store/actions/services";
import LoadingScreen from "../../../../components/LoadingScreen/LoadingScreen";
import { BookingsHeader } from "../BookingsHeader/BookingsHeader";
import { isActiveUserEngineerAtom } from "../../../../../atoms/user/activeUserAtom";

export const BookingsScreenLayout = () => {
  const match = useMatch(`${ROUTE_PREFIXES.BOOKINGS}/:tab`);
  const navigate = useNavigate();
  const activeProfile = useAtomValue(activeProfileAtom);
  const dispatch = useAppDispatch();
  const activeStudioProfile = useAtomValue(activeStudioProfileAtom);
  const isEngineer = useAtomValue(isActiveUserEngineerAtom);

  const selectedTab = useMemo(() => {
    return match?.params.tab;
  }, [match]);

  const handleTabClick = useCallback(
    (_: React.SyntheticEvent, tabValue: string) => {
      emitAnalyticsTrackingEvent("my_bookings_tab_clicked", {
        tab: tabValue,
      });
      navigate(`${ROUTE_PREFIXES.BOOKINGS}/${tabValue}`);
    },
    [navigate],
  );

  const [isLoadingMyEngineeringServices, setIsLoadingMyEngineeringServices] =
    useState(false);
  const { user, userStudiosLoading } = useAppSelector(
    (state) => state.accountInfo,
  );
  const engineerServicesLoaded = useAppSelector(
    selectEngineerServicesForCurrentEngineerLoaded(user?.engineer?.id),
  );

  useSetPageTitle("Bookings");

  useEffect(() => {
    if (
      !user?.engineer ||
      Boolean(user.engineer.deleted) ||
      engineerServicesLoaded
    )
      return;

    setIsLoadingMyEngineeringServices(true);
    void dispatch(
      loadEngineerServices({ engineer_id: user.engineer.id }),
    ).finally(() => {
      setIsLoadingMyEngineeringServices(false);
    });
  }, [dispatch, user, engineerServicesLoaded]);

  if (isLoadingMyEngineeringServices || userStudiosLoading) {
    return <LoadingScreen />;
  }

  return (
    <BookingsScreenContainerWrapper>
      <BookingsScreenContainer>
        <BookingsHeader selectedTab={selectedTab} />
        <Box
          sx={(theme) => ({
            position: "relative",
            alignSelf: "flex-start",
            maxWidth: "100%",

            [theme.breakpoints.down("sm")]: {
              alignSelf: "center",
            },
          })}
        >
          <TabsBottomGreyBar />

          <EETabs
            value={selectedTab}
            onChange={handleTabClick}
            variant="scrollable"
            scrollButtons={false}
          >
            {activeProfile?.type === ProfileType.USER && (
              <StyledBookingTab
                disableRipple
                value={BookingTabs.Projects}
                label={<BookingsScreenProjectsTabLabel />}
                {...a11yProps(BookingTabs.Projects)}
              />
            )}
            <StyledBookingTab
              disableRipple
              value={BookingTabs.Sessions}
              label={<BookingsScreenSessionsTabLabel />}
              {...a11yProps(BookingTabs.Sessions)}
            />
            {activeStudioProfile || isEngineer ? (
              <StyledBookingTab
                disableRipple
                value={BookingTabs.Links}
                label={"Links"}
                {...a11yProps(BookingTabs.Links)}
              />
            ) : null}
          </EETabs>
        </Box>
        <Outlet />
      </BookingsScreenContainer>
      <BookingsScreenFooterWrapper>
        <DevLinkProvider>
          <EngineEarsFooter2024 />
        </DevLinkProvider>
      </BookingsScreenFooterWrapper>
    </BookingsScreenContainerWrapper>
  );
};
